import { createContext, useCallback, useContext, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  invalidation,
  isApiError,
  liveState,
  message,
  type AccountInfo,
  type ApiClient,
  type LiveState,
} from '@sl/core';
import { useToast } from '../../ui/Toast/ToastProvider';
import { useClock } from '../clock/ClockProvider';
import { messageText } from '../messages';
import { SHELL_COPY } from './copy';
import type { ShellContextValue } from './shellContext';

// **THE SHELL'S ONE READ OF `meta` (T-211, on T-208's `liveState` — D-1078).**
//
// Three surfaces say something about the same answer: the freshness label and its refresh control in the top
// bar (`Freshness`), §12.2's band under it (`ConnectionBand`) and REQ-002 AC2 / REQ-283 AC2's own banners
// (`LiveStates`). Before this hook each one would have had to read `§2.3` for itself, which is the shape
// D-1078 found five copies of — and two readers computing `liveState` a millisecond apart can disagree about
// API §1.6's sixty-minute line, so the label could say amber while the banner said nothing.
//
// So the status is read ONCE, here, `liveState` is derived ONCE from it, and the three surfaces take the typed
// result as a prop. The refresh REQUEST lives here too, because two controls ask for it — the top bar's
// control (REQ-283 AC1) and the "Try again" on the three-hour banner (w17 §6) — and a press in either place
// must invalidate the same way and say the same thing afterwards.
//
// Nothing here reads a field of `meta` or `SyncStatus` itself; the two exceptions are named where they are.

export interface ShellLive {
  /** T-208's typed state of the answer: freshness + tone + banner, the band, the warnings, the rate-limit
   *  sentence, the refresh limit, the first sync, offline, and today's partial. */
  state: LiveState;
  /**
   * `meta.source === 'pull'` — which refresh sentence the control is allowed to promise (T-287). It is read
   * off the envelope rather than off `AppConfig`, because a response that has not arrived yet promises
   * nothing special and the app's own `?dataset=` is a guess about the server dressed as a fact.
   */
  pulled: boolean;
  /** A refresh this app asked for is in flight — distinct from §2.3's own `state: 'running'`. */
  requesting: boolean;
  /** REQ-283 AC1's refresh. Honours nothing itself: `state.refresh` is what says whether it may be pressed. */
  refresh: () => void;
}

export interface ShellLiveInput {
  /**
   * The shell context VALUE, passed rather than read with `useShell()`, so this hook can be called in the
   * component that builds it — which is the only place it can be called once for all three surfaces.
   */
  shell: ShellContextValue;
  account: AccountInfo;
  client: ApiClient;
  /** What the client says about the DEVICE (`ApiClient.connectivity()`), already tracked by the shell. */
  offline: boolean;
}

export function useShellLive({ shell, account, client, offline }: ShellLiveInput): ShellLive {
  const clock = useClock();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [requesting, setRequesting] = useState(false);
  // The live label comes from the sync status of T-021 / T-023: `queries.syncStatus(id)` polls itself while a
  // sync is running, so this only observes it.
  const sync = useQuery({ ...shell.queries.syncStatus(account.id), retry: false });
  const pulled = sync.data?.meta.source === 'pull';

  const state = liveState({
    account,
    sync: sync.data?.data,
    // The envelope is passed as well as the body: §2.3's own `dataAsOf` still wins, and where a first sync has
    // none the answer's `meta.dataAsOf` is the next honest instant (D-1078 (c)). `meta.stale` and
    // `warnings[]` are server facts that can only ADD to the client's own age rule (D-1078 (b)).
    meta: sync.data?.meta,
    // REQ-246 AC5: the CLIENT is the only thing that knows, and a demo account is never offline whatever it
    // says — `offlineState` applies that rule, not this call site.
    connectivity: offline ? 'offline' : 'online',
    now: clock.now(),
    ctx: shell.messages,
    deviceTimeZone: shell.deviceTimeZone,
  });

  const refresh = useCallback(() => {
    void (async () => {
      setRequesting(true);
      try {
        await client.requestSync(account.id);
        await queryClient.invalidateQueries(invalidation.requestSync(account.id));
        // The toast is the ONE announcement (T-035): a refresh moves no focus, so a screen reader hears this.
        toast({
          message: pulled ? SHELL_COPY.refreshDonePull : SHELL_COPY.refreshDone,
          tone: 'success',
          key: 'shell.refresh',
        });
      } catch (error) {
        const sentence = isApiError(error) ? error.userMessage : message('error.unknown');
        toast({
          message: messageText(sentence, shell.messages),
          tone: 'error',
          key: 'shell.refresh',
        });
      } finally {
        setRequesting(false);
      }
    })();
  }, [account.id, client, pulled, queryClient, shell.messages, toast]);

  return { state, pulled, requesting, refresh };
}

// ─── the context a ROUTE reads (T-428, on T-422's note (5) — D-1115 (f), D-1120) ────────────────────────────
//
// The three top-bar surfaces take `live` as a PROP, because the shell builds it and they are its own
// children. A route is not: it arrives through `<Outlet/>`, so until this context Home re-observed
// `queries.syncStatus(account.id)` for itself to learn a fact the shell had already resolved — the same
// query key and no second network read, but a second derivation of one truth, which is the shape D-1078
// found five copies of. The phone has had `ShellLiveProvider` since T-214 and every surface reads it with
// `useLive()`; this is the web's half of that shape, and it is what serves the four slots of Home that
// each read a `409 not_ready` (T-428).
//
// `useShellLiveMaybe()` is deliberately the only reader, and it never throws: the tile, the briefing and
// the chart slot are rendered in component tests and in the kitchen sink with no shell around them, where
// "no live state" is an honest answer. A caller treats `null` as "nothing known about a first sync", which
// is also exactly what a demo build is.

// The context is an export rather than a `<ShellLiveProvider>` wrapper for the reason `shellContext.ts`
// already is one: this module is a `.ts` file with no JSX in it, and the shell mounts the provider itself
// beside the four it already mounts.
export const ShellLiveContext = createContext<ShellLive | null>(null);

/** The shell's one live state, or `null` outside the shell (a component test, the kitchen sink). */
export function useShellLiveMaybe(): ShellLive | null {
  return useContext(ShellLiveContext);
}

/** Is a FIRST SYNC running, as the shell already resolved it? `false` wherever nothing is known. */
export function useFirstSyncRunning(): boolean {
  return useShellLiveMaybe()?.state.firstSync != null;
}
